import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GenerateButton } from '../EditInfluencerPage.styles';
import { PriceGroup, PriceInput, PriceRow, PricingForm } from './PricingSection.styles';
import { Text14Regular, Text16Regular } from '../../../../components/utils/Text/Text.styles';
import { selectChatterActionsPending } from '../../../../store/slices/chatter/slice';
import { editInfluencerPricing } from '../../../../store/slices/chatter/asyncThunks';
import { notifyError, notifyInfo } from '../../../../utils/notify';

// Must match price limits on the server (1 star = $0.02, max 25000 stars)
const MIN_PRICE_IN_USD = 0.02;
const MAX_PRICE_IN_USD = 500;

const priceSchema = yup
  .number()
  .transform((value, originalValue) => (originalValue === '' || Number.isNaN(value) ? undefined : value))
  .typeError('Required')
  .required('Required')
  .min(MIN_PRICE_IN_USD, `Min $${MIN_PRICE_IN_USD}`)
  .max(MAX_PRICE_IN_USD, `Max $${MAX_PRICE_IN_USD}`);

const rangeSchema = yup.object({
  min: priceSchema,
  max: priceSchema.moreThan(yup.ref('min'), 'Enter a higher value'),
});

const pricingFormSchema = yup.object({
  photo: rangeSchema,
  video: rangeSchema,
});

const toFormValues = boundaries => ({
  photo: { min: boundaries?.photo?.min ?? '', max: boundaries?.photo?.max ?? '' },
  video: { min: boundaries?.video?.min ?? '', max: boundaries?.video?.max ?? '' },
});

const MEDIA_TYPES = [
  { key: 'photo', title: 'Photo Price Range' },
  { key: 'video', title: 'Video Price Range' },
];

const PricingSection = ({ influencer }) => {
  const dispatch = useDispatch();
  const { editInfluencerPricing: savePending } = useSelector(selectChatterActionsPending);
  const boundaries = influencer.pricing?.boundaries;
  const isLocked = !!influencer.hasMedia;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    delayError: 300,
    defaultValues: toFormValues(boundaries),
    resolver: yupResolver(pricingFormSchema),
  });

  useEffect(() => {
    reset(toFormValues(boundaries));
  }, [boundaries, reset]);

  const onSubmit = data => {
    dispatch(editInfluencerPricing({ influencerId: influencer.id, boundaries: data }))
      .unwrap()
      .then(() => notifyInfo('Pricing saved successfully!'))
      .catch(err => notifyError(err.message));
  };

  if (isLocked) {
    return <Text14Regular>Pricing cannot be changed when media has already been uploaded.</Text14Regular>;
  }

  return (
    <PricingForm onSubmit={handleSubmit(onSubmit)}>
      <Text14Regular>Set the price range (in USD) for paid photos and videos sent by the creator.</Text14Regular>
      {MEDIA_TYPES.map(({ key, title }) => (
        <PriceGroup key={key}>
          <Text16Regular>{title}</Text16Regular>
          <PriceRow>
            <PriceInput
              label="Min"
              prefix="$"
              disabled={savePending}
              {...register(`${key}.min`)}
              error={errors[key]?.min?.message}
            />
            <PriceInput
              label="Max"
              prefix="$"
              disabled={savePending}
              {...register(`${key}.max`)}
              error={errors[key]?.max?.message}
            />
          </PriceRow>
        </PriceGroup>
      ))}
      <GenerateButton title="Save" type="submit" disabled={!isDirty} isLoading={savePending} />
    </PricingForm>
  );
};

export default PricingSection;
